import { useEffect, useMemo, useState } from 'react'
import BidMsrpButton from '../../../../elements/BidMsrpButton/BidMsrpButton'
import Section from '@components/ItemPage/Layout/Section'
import { useItemPage } from '@components/ItemPage/Layout/ItemPageContext'
import Link from 'next/link'

const LOCATION_VISIBILITY_THRESHOLD_MS = 48 * 60 * 60 * 1000

const Information = () => {
  const { itemState: item, auction } = useItemPage()

  const { utcEndDateTime, itemClosed } = item
  const [currentTime, setCurrentTime] = useState<number>(() => Date.now())

  useEffect(() => {
    setCurrentTime(Date.now())
  }, [utcEndDateTime])

  const auctionEndTime = useMemo(() => {
    if (!utcEndDateTime) return undefined
    const parsedDate = new Date(utcEndDateTime).getTime()
    return Number.isNaN(parsedDate) ? undefined : parsedDate
  }, [utcEndDateTime])

  const shouldPollForVisibility = useMemo(() => {
    if (!auctionEndTime) return false
    if (itemClosed) return false
    return auctionEndTime - currentTime > LOCATION_VISIBILITY_THRESHOLD_MS
  }, [auctionEndTime, currentTime, itemClosed])

  useEffect(() => {
    if (!shouldPollForVisibility) return

    const interval = setInterval(() => {
      setCurrentTime(Date.now())
    }, 60 * 1000)

    return () => clearInterval(interval)
  }, [shouldPollForVisibility])

  const shouldShowLocation = useMemo(() => {
    if (itemClosed) return true
    if (!auctionEndTime) return true
    return auctionEndTime - currentTime <= LOCATION_VISIBILITY_THRESHOLD_MS
  }, [auctionEndTime, currentTime, itemClosed])

  const formatBinLocation = (binLocation?: string) => {
    if (!binLocation) return undefined
    const [apc, aisle, bay, level] = binLocation.split('-')
    const lines: string[] = []
    if (apc) lines.push(`${apc} - Auction Pickup Center`)
    if (aisle) lines.push(`${aisle.padStart(2, '0')} - Aisle`)
    if (bay) lines.push(`${bay.padStart(2, '0')} - Bay`)
    if (level) lines.push(`${level.padStart(2, '0')} - Level`)
    return lines.join('\n')
  }

  const {
    msrp,
    brand,
    damageDescription,
    condition,
    description,
    unitDescription,
    model,
    specs,
    lotCode,
  } = item

  const itemInformation: {
    [key: string]: string | number | undefined
  } = {
    Brand: brand,
    'Damage Description': damageDescription,
    Condition: condition,
    Description: description,
    'Unit Quantity Description': unitDescription,
    Model: model,
    Specs: specs,
    'Lot Code': lotCode,
    Width: item?.width,
    Depth: item?.depth,
    Weight: item?.weight,
    Length: item?.length,
    'Additional Info': item?.condition,
    'Pickup Location': `${auction?.location?.address}, ${auction?.location?.city} ${auction?.location?.state} ${auction?.location?.zip}`,
    ...(shouldShowLocation && {
      'Warehouse Location': item?.auctionWarehouseLocation,
    }),
    ...(shouldShowLocation &&
      item?.binMasterLocation && {
        'Item Location': formatBinLocation(item?.binMasterLocation),
      }),
  }

  return (
    <Section title="Item Information" canClose={true}>
      <div className="text-sm">
        <div className="w-full">
          <div className="flex gap-2 p-2 border-b-[1px] border-gray-100">
            <div className="font-bold flex items-start gap-1 w-40">
              MSRP: <BidMsrpButton />
            </div>
            <div className="w-full">${msrp?.toFixed(2)}</div>
          </div>
          {Object.keys(itemInformation)
            .filter((key) => itemInformation[key] || key === 'Description')
            .map((key, index) => {
              return (
                <div
                  key={key}
                  className="flex gap-2 p-2 border-b-[1px] border-gray-100 w-full"
                >
                  <div className="font-bold w-40">{key || ''}:</div>
                  <div
                    className="w-full whitespace-pre-line"
                    style={{ wordBreak: 'break-word' }}
                  >
                    {itemInformation?.[key] || 'N/A'}
                  </div>
                </div>
              )
            })}
          <div className="flex gap-2 p-2 border-b-[1px] border-gray-100">
            <div className="font-bold flex items-start gap-1 w-40">
              Auction Number
            </div>
            <Link
              href={`/auction-detail/${auction.id}/1`}
              className="hover:underline text-bidfta-blue-light font-bold w-full"
            >
              {auction.auctionNumber}
            </Link>
          </div>
        </div>
      </div>
    </Section>
  )
}

export default Information
